import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { SwipaWordmark } from '@/icons';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { authApi } from '@/api/auth';
import { extractError } from '@/api/client';

const schema = z
  .object({
    password: z
      .string()
      .min(8, 'Минимум 8 символов')
      .regex(/[A-Z]/, 'Должна быть заглавная буква')
      .regex(/[0-9]/, 'Должна быть цифра'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Пароли не совпадают',
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof schema>;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [serverError, setServerError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    setServerError('');
    try {
      await authApi.resetPassword(token, data.password);
      navigate('/auth/login', { state: { message: 'Пароль изменён. Войдите в аккаунт.' } });
    } catch (err) {
      setServerError(extractError(err));
    }
  };

  return (
    <div className="min-h-screen bg-surface-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <SwipaWordmark size={44} base="#ffffff" accent="#C5243A" />
          <p className="text-surface-400 mt-3 text-sm">Задайте новый пароль</p>
        </div>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="bg-surface-900 border border-surface-800 rounded-xl p-6 space-y-4"
        >
          {serverError && (
            <div className="bg-red-900/40 border border-red-700 text-red-300 text-sm rounded-lg px-3 py-2">
              {serverError}
            </div>
          )}
          <div>
            <label className="block text-surface-300 text-xs font-medium mb-1.5">
              Новый пароль
            </label>
            <input
              {...register('password')}
              type="password"
              className="irc-input"
              placeholder="••••••••"
            />
            {errors.password && (
              <p className="text-red-400 text-xs mt-1">{errors.password.message}</p>
            )}
          </div>
          <div>
            <label className="block text-surface-300 text-xs font-medium mb-1.5">
              Подтвердите пароль
            </label>
            <input
              {...register('confirmPassword')}
              type="password"
              className="irc-input"
              placeholder="••••••••"
            />
            {errors.confirmPassword && (
              <p className="text-red-400 text-xs mt-1">{errors.confirmPassword.message}</p>
            )}
          </div>
          <button type="submit" disabled={isSubmitting || !token} className="btn-primary w-full">
            {isSubmitting ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              'Сохранить пароль'
            )}
          </button>
          <p className="text-center text-surface-400 text-sm">
            <Link to="/auth/login" className="text-brand-400 hover:text-brand-300">
              Назад к входу
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
